import axios from "axios";
import { IBehaviorLocalhostConfiguration, IResponseWaitingConfiguration } from "../../../domain/configuration";
import { Dictionary } from "../../../domain/system";
import { UrlSchemeClient } from "../../../utilities";
import { LoggerFactory } from "../../../utilities/logger";

export class ResponsibleUrlSchemeClient {
  constructor(private _getToken: () => string, private readonly _configuration: IBehaviorLocalhostConfiguration) {}

  private readonly _logger = LoggerFactory.getLogger();

  async request<TRequest, TResult>(action: string, request: TRequest, waitingConfiguration: IResponseWaitingConfiguration): Promise<TResult> {
    const key = await this.postRequest(request);
    if (!key) {
      throw new Error("要求登録失敗しました.");
    }
    this.requestResponsibleAction(action, key);
    return this.getResponse(key, waitingConfiguration);
  }

  private async postRequest<TRequest>(request: TRequest): Promise<string> {
    const url = this._configuration.httpUrlPrefix + "/api/local/register";
    this._logger.debug("[POST] " + url);
    const result = await axios.post(
      url,
      request,
      {
        params: {
          token: this.token,
        },
      });
    this._logger.debug(result.data);
    if (!result.data) {
      throw new Error("キー情報の登録に失敗しました.");
    }
    return result.data.key;
  }

  private get token(): string {
    const token = this._getToken();
    if (!token) {
      throw new Error("token is empty!!!");
    }
    return token;
  }

  private async requestResponsibleAction(action: string, key: string): Promise<void> {
    const parameter = new Dictionary<string, string>();
    parameter.set("action", action);
    parameter.set("token", this.token);
    parameter.set("key", key);
    const query = parameter.keys.map(k => k + "=" + parameter.get(k)).join("&");
    const url = this._configuration.schemeUrlPrefix + "?" + query;
    const client = new UrlSchemeClient();
    client.request(url);
  }

  private async getResponse<TResponse>(key: string, waitingConfiguration: IResponseWaitingConfiguration): Promise<TResponse> {
    let result: { isOk: boolean, result: any } = null;
    let counter = 0;

    const getReponse = async (): Promise<void> => {
      if (waitingConfiguration.maxCount >= 0) {
        counter++;
        if (counter > waitingConfiguration.maxCount) { 
          this._logger.warn("結果取得待ちでタイムアウトしました。");
          result = {
            isOk: false,
            result: new Error("Assistantからの応答がタイムアウトしました.(" + waitingConfiguration.maxCount + "/" + waitingConfiguration.intervalMilliSeconds + ")"),
          };
          return;
        }
      }
      if (!!result) {
        return;
      }
      try {
        const response = await this.getResultAsAny(key);
        if (!response) {
          setTimeout(getReponse, waitingConfiguration.intervalMilliSeconds);
          return;
        }
        result = {
          isOk: true,
          result: response,
        };
      } catch (e) {
        this._logger.error(e);
        result = {
          isOk: false,
          result: new Error("Assistantから結果取得中に予期せぬエラーが発生しました."),
        };
        return;
      }
    };
    setTimeout(getReponse, 500);

    while (!result) {
      await this.sleep(100);
    }
    if (result.isOk) {
      return result.result;
    } else {
      throw result.result;
    }
  }

  private async getResultAsAny(key: string): Promise<any> {
    // 一回目の問い合わせでブラウザ内にキャッシュが作られてしまってapiに問い合わせに行かなくなってしまうので時刻を付与して暫定対応している
    const suppressor = Date.now().toString(16);
    const query = {
      params: {
        token: this.token,
        suppressor,
      },
    };
    const url = this._configuration.httpUrlPrefix + "/api/local/result/" + key;
    this._logger.debug("[GET] " + url);
    const result = await axios.get(url, query);
    this._logger.debug(result.data);
    return result.data;
  }

  private async sleep(msec: number) {
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    await sleep(msec);
  }
}
